import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Chip } from "@mui/material";
import InternationalSourcingIcon from "../../../assets/IntSourcing.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
    r / 255
  } 0 0 0 0 ${g / 255} 0 0 0 0 ${
    b / 255
  } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

const ScriptCoMemberDetails = ({ opportunity }) => {
  // Extract fields from the opportunity
  const drugName = opportunity?.drug
    ? capitalizeFirstLetter(opportunity.drug)
    : "";
  const strength =
    opportunity?.strength && opportunity.strength !== "N/A"
      ? opportunity.strength
      : "";
  const category = opportunity?.category || "";
  const ndc =
    opportunity?.drugNdc && opportunity.drugNdc !== "N/A"
      ? opportunity.drugNdc
      : "";
  const memberId =
    opportunity?.member?.subscriberNum || opportunity?.subscriberNum || "";
  const vendorReferenceId =
    opportunity?.vendorReferenceId ||
    opportunity?.vendorPreferenceId ||
    opportunity?.program?.vendorReferenceId;

  const personCode =
    opportunity?.member?.personCode || opportunity?.personCode || "";

  const relCode = opportunity?.member?.relCode || opportunity?.relCode || "";

  const carrierName = capitalizeFirstLetter(
    opportunity?.member?.carrierName || opportunity?.member?.carrier || "",
  );

  const groupNameRaw = capitalizeFirstLetter(
    opportunity?.member?.groupName || opportunity?.groupName || "",
  );
  const groupNum =
    opportunity?.member?.groupNum ||
    opportunity?.member?.groupId ||
    opportunity?.groupNum ||
    "";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "15px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Drug Title & Badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", color: "#101828" }}
          >
            {drugName || "N/A"}{" "}
            {strength && <span style={{ color: "#403C6F" }}>{strength}</span>}
          </Typography>
          {category && (
            <Chip
              size="small"
              icon={
                <img
                  src={InternationalSourcingIcon}
                  alt="ScriptCo Sourcing"
                  style={{
                    width: "14px",
                    height: "14px",
                    filter: getSvgFilter("#403C6F"),
                    marginLeft: "8px",
                  }}
                />
              }
              label={category}
              sx={{
                backgroundColor: "#EFEFF4",
                color: "#403C6F",
                fontWeight: 600,
                fontSize: "12px",
                height: "24px",
                borderRadius: "40px",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          )}
        </Box>

        {/* Info Rows */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "#4A5565",
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <span>
            NDC: <span style={{ fontWeight: 600 }}>{ndc || "N/A"}</span>
          </span>
          <span>•</span>
          <span>
            Member ID:{" "}
            <span style={{ fontWeight: 600 }}>{memberId || "N/A"}</span>
          </span>
          <span>•</span>
          <span>
            Person Code:{" "}
            <span style={{ fontWeight: 600 }}>{personCode || "N/A"}</span>
          </span>
          <span>•</span>
          <span>
            Relationship Code:{" "}
            <span style={{ fontWeight: 600 }}>{relCode || "N/A"}</span>
          </span>
        </Typography>

        <Typography
          sx={{
            fontSize: "12px",
            color: "#4A5565",
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <span>
            Carrier: <span style={{ fontWeight: 600 }}>{carrierName}</span>
          </span>
          <span>•</span>
          <span>
            Group Name: <span style={{ fontWeight: 600 }}>{groupNameRaw}</span>
          </span>
          <span>•</span>
          <span>
            Group ID: <span style={{ fontWeight: 600 }}>{groupNum}</span>
          </span>
        </Typography>
      </Box>

      {/* ScriptCo Assigned Badge */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          backgroundColor: "#F4FBFA",
          borderRadius: "8px",
          px: 2,
          py: 1,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "20px", color: "#1E3A8A" }}
        >
          {vendorReferenceId ? `ScriptCo #${vendorReferenceId}` : "ScriptCo"}
        </Typography>
      </Box>
    </Box>
  );
};

ScriptCoMemberDetails.propTypes = {
  opportunity: PropTypes.object,
};

export default ScriptCoMemberDetails;
